'use client';
import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Target, Shield, Clock, AlertTriangle, ChevronDown, ChevronUp, Copy, Bookmark, Share2, CheckCircle2, Timer } from 'lucide-react';
import { toast } from 'sonner';
import type { SignalData, TPLevel } from '../utils/signalGenerator';
import SignalScoreRing from './SignalScoreRing';
import { useLanguage } from '@/contexts/LanguageContext';

interface Props {
  signal: SignalData;
}

function useCountdown(validUntilTime: string): { display: string; urgent: boolean; expired: boolean } {
  const [remaining, setRemaining] = useState<number>(() => calcRemaining(validUntilTime));

  function calcRemaining(timeStr: string): number {
    const now = new Date();
    const [hStr, mStr] = timeStr.split(':');
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    const target = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), h, m, 0));
    let diff = target.getTime() - now.getTime();
    // If already past today's time, it expired
    if (diff < 0) diff = 0;
    return Math.floor(diff / 1000);
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(calcRemaining(validUntilTime));
    }, 1000);
    return () => clearInterval(interval);
  }, [validUntilTime]);

  const expired = remaining <= 0;
  const urgent = remaining > 0 && remaining <= 300; // last 5 minutes

  if (expired) {
    return { display: 'EXPIRED', urgent: false, expired: true };
  }

  const hrs = Math.floor(remaining / 3600);
  const mins = Math.floor((remaining % 3600) / 60);
  const secs = remaining % 60;
  const pad = (n: number) => String(n).padStart(2, '0');

  const display = hrs > 0
    ? `${pad(hrs)}:${pad(mins)}:${pad(secs)}`
    : `${pad(mins)}:${pad(secs)}`;

  return { display, urgent, expired };
}

export default function SignalCard({ signal }: Props) {
  const [expandedTPs, setExpandedTPs] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);
  const { t } = useLanguage();
  const countdown = useCountdown(signal.timing.validUntilTime);

  const isBuy = signal.direction === 'BUY';

  const directionColor = isBuy ? 'text-emerald-400' : 'text-rose-400';
  const directionBg = isBuy ? 'bg-emerald-500/10' : 'bg-rose-500/10';
  const directionBorder = isBuy ? 'border-emerald-500/25' : 'border-rose-500/25';
  const directionGlow = isBuy ? 'card-glow-buy' : 'card-glow-sell';
  const directionPulse = isBuy ? 'bg-emerald-400' : 'bg-rose-400';
  const accentGradient = isBuy
    ? 'from-emerald-500/0 via-emerald-400 to-emerald-500/0' :'from-rose-500/0 via-rose-400 to-rose-500/0';

  const handleCopy = () => {
    const text = `XAU Engine AI Pro Signal\n${signal.direction} XAU/USD M15\nEntry: ${signal.entryZone.best.low}–${signal.entryZone.best.high}\nSL: ${signal.tradeSL}\nTP1: ${signal.tpLevels[0].price} | TP2: ${signal.tpLevels[1].price} | TP3: ${signal.tpLevels[2].price}\nAI Score: ${signal.aiScore} | Confidence: ${signal.confidenceScore}%\nGenerated: ${signal.generatedAt}\n\nTrade on XAU Engine AI Pro: ${process.env.NEXT_PUBLIC_SITE_URL ?? 'https://xauengine5152.builtwithrocket.new'}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Signal copied to clipboard', { duration: 2500 });
  };

  const handleShare = async () => {
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? 'https://xauengine5152.builtwithrocket.new';
    const shareText = `🚨 ${signal.direction} XAU/USD Signal — AI Score ${signal.aiScore} | Confidence ${signal.confidenceScore}%\nEntry: ${signal.entryZone.best.low}–${signal.entryZone.best.high} | SL: ${signal.tradeSL}\n\nJoin XAU Engine AI Pro for live gold signals:`;
    const shareUrl = siteUrl;

    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: 'XAU Engine AI Pro Signal', text: shareText, url: shareUrl });
        setShared(true);
        setTimeout(() => setShared(false), 2000);
      } catch {
        // User cancelled or share failed — fall back to clipboard
        await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        setShared(true);
        setTimeout(() => setShared(false), 2000);
        toast.success('Invite link copied to clipboard', { duration: 2500 });
      }
    } else {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      setShared(true);
      setTimeout(() => setShared(false), 2000);
      toast.success('Invite link copied to clipboard', { duration: 2500 });
    }
  };

  const visibleTPs = expandedTPs ? signal.tpLevels : signal.tpLevels.slice(0, 3);

  return (
    <div className={`rounded-xl border bg-[#0f1117] ${directionBorder} ${directionGlow} signal-card-enter overflow-hidden`}>
      {/* Top accent bar */}
      <div className={`h-[2px] w-full bg-gradient-to-r ${accentGradient}`} />

      <div className="p-3 sm:p-5 space-y-4 sm:space-y-5">
        {/* ─── Header Row ─── */}
        <div className="flex items-start justify-between gap-2 flex-wrap">
          {/* Direction badge */}
          <div className="flex items-start gap-2 sm:gap-3 min-w-0">
            <div
              className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-lg border flex-shrink-0 ${directionBg} ${directionBorder} shadow-lg ${isBuy ? 'shadow-emerald-500/10' : 'shadow-rose-500/10'}`}
            >
              {isBuy ? (
                <TrendingUp className={`w-4 h-4 ${directionColor}`} />
              ) : (
                <TrendingDown className={`w-4 h-4 ${directionColor}`} />
              )}
              <span className={`text-sm font-bold ${directionColor} tracking-widest font-mono`}>
                {signal.direction}
              </span>
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold text-zinc-200 font-mono tracking-wide">XAU/USD</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-zinc-800 border border-white/8 font-mono text-zinc-400 tracking-widest">
                  M15
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-1">
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${directionPulse} pulse-gold`} />
                <span className="text-[10px] text-zinc-500 font-mono break-all">
                  #{signal.id} · {signal.generatedAt}
                </span>
              </div>
              {signal.currentPrice && signal.currentPrice !== 'N/A' && (
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  <span className="text-xs text-zinc-300 font-mono uppercase tracking-widest">{t('priceAtSignal')}</span>
                  <span className="text-base font-bold font-mono text-zinc-100 tabular-nums">{signal.currentPrice}</span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-yellow-600/30 border border-yellow-500 animate-pulse">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-yellow-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                    <span className="text-[10px] sm:text-xs font-extrabold text-yellow-300 uppercase tracking-wide whitespace-nowrap">⚡ ONCE PROFIT — CLOSE</span>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Badges + Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleCopy}
              className="w-7 h-7 flex items-center justify-center rounded-md bg-zinc-800/80 border border-white/8 hover:bg-zinc-700 hover:border-white/15 hover:-translate-y-0.5 text-zinc-400 hover:text-zinc-200 transition-all duration-150 active:scale-95"
              title="Copy signal details"
            >
              {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
            <button className="w-7 h-7 flex items-center justify-center rounded-md bg-zinc-800/80 border border-white/8 hover:bg-zinc-700 hover:border-white/15 hover:-translate-y-0.5 text-zinc-400 hover:text-zinc-200 transition-all duration-150 active:scale-95" title="Bookmark">
              <Bookmark className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={handleShare}
              className="w-7 h-7 flex items-center justify-center rounded-md bg-zinc-800/80 border border-white/8 hover:bg-zinc-700 hover:border-white/15 hover:-translate-y-0.5 text-zinc-400 hover:text-zinc-200 transition-all duration-150 active:scale-95"
              title="Invite peers to XAU Engine AI Pro"
            >
              {shared ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        {/* ─── Score Trio ─── */}
        <div className="grid grid-cols-2 gap-3">
          <SignalScoreRing
            label={t('aiScore')}
            value={signal.aiScore}
            max={100}
            color={signal.aiScore >= 80 ? '#34d399' : signal.aiScore >= 60 ? '#fbbf24' : '#f87171'}
            suffix=""
            description="Composite algorithmic strength"
          />
          <SignalScoreRing
            label="Confidence"
            value={signal.confidenceScore}
            max={100}
            color={isBuy ? '#34d399' : '#fb7185'}
            suffix="%"
            description={`${signal.direction} direction probability`}
          />
        </div>

        {/* ─── Entry Zones ─── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Best Entry */}
          <div className={`rounded-lg border p-3 sm:p-3.5 ${isBuy ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-rose-500/5 border-rose-500/20'}`}>
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-1.5">
                <Target className={`w-3.5 h-3.5 ${directionColor}`} />
                <span className="text-xs font-mono font-bold text-zinc-200 uppercase tracking-widest">{t('bestEntry')}</span>
              </div>
              <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded ${isBuy ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'} tracking-widest`}>{t('ideal')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 text-center bg-black/20 rounded-md py-1.5">
                <p className="text-[9px] text-zinc-500 font-mono mb-0.5">{t('low')}</p>
                <p className="text-sm sm:text-base font-bold font-mono text-zinc-100 tabular-nums">{signal.entryZone.best.low}</p>
              </div>
              <div className={`text-[10px] font-mono ${directionColor} font-bold flex-shrink-0`}>—</div>
              <div className="flex-1 text-center bg-black/20 rounded-md py-1.5">
                <p className="text-[9px] text-zinc-500 font-mono mb-0.5">{t('high')}</p>
                <p className="text-sm sm:text-base font-bold font-mono text-zinc-100 tabular-nums">{signal.entryZone.best.high}</p>
              </div>
            </div>
            <p className="text-[10px] text-zinc-500 font-mono text-center mt-2">{signal.entryZone.best.spread} {t('pipSpread')}</p>
          </div>

          {/* OK Entry */}
          <div className="rounded-lg border border-amber-500/15 bg-amber-500/5 p-3 sm:p-3.5">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5 text-amber-400/80" />
                <span className="text-xs font-mono font-bold text-zinc-200 uppercase tracking-widest">{t('okEntry')}</span>
              </div>
              <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 tracking-widest">{t('ok')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 text-center bg-black/20 rounded-md py-1.5">
                <p className="text-[9px] text-zinc-500 font-mono mb-0.5">{t('low')}</p>
                <p className="text-sm sm:text-base font-bold font-mono text-zinc-100 tabular-nums">{signal.entryZone.ok.low}</p>
              </div>
              <div className="text-[10px] font-mono text-amber-400/60 font-bold flex-shrink-0">—</div>
              <div className="flex-1 text-center bg-black/20 rounded-md py-1.5">
                <p className="text-[9px] text-zinc-500 font-mono mb-0.5">{t('high')}</p>
                <p className="text-sm sm:text-base font-bold font-mono text-zinc-100 tabular-nums">{signal.entryZone.ok.high}</p>
              </div>
            </div>
            <p className="text-[10px] text-zinc-500 font-mono text-center mt-2">{signal.entryZone.ok.spread} {t('pipSpread')}</p>
          </div>
        </div>

        {/* ─── Stop Loss ─── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-center justify-between px-3 sm:px-4 py-3 rounded-lg bg-zinc-800/40 border border-white/8">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-md bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Shield className="w-3.5 h-3.5 text-amber-400" />
              </div>
              <div>
                <p className="text-[10px] font-mono text-zinc-200 uppercase tracking-widest font-bold">{t('tradeSl')}</p>
                <p className="text-[10px] text-zinc-400 font-mono">{t('initialStop')}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold font-mono text-zinc-100 tabular-nums">{signal.tradeSL}</p>
              <p className="text-[10px] font-mono text-zinc-400">{signal.tradeSLPips} {t('pips')}</p>
            </div>
          </div>
          <div className="flex items-center justify-between px-3 sm:px-4 py-3 rounded-lg bg-rose-950/20 border border-rose-500/20">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-md bg-rose-500/10 border border-rose-500/20 flex items-center justify-center flex-shrink-0">
                <Shield className="w-3.5 h-3.5 text-rose-400" />
              </div>
              <div>
                <p className="text-[10px] font-mono text-zinc-200 uppercase tracking-widest font-bold">{t('hardSl')}</p>
                <p className="text-[10px] text-zinc-400 font-mono">{t('maxLoss')}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold font-mono text-zinc-100 tabular-nums">{signal.hardSL}</p>
              <p className="text-[10px] font-mono text-zinc-400">{signal.hardSLPips} {t('pips')}</p>
            </div>
          </div>
        </div>

        {/* ─── No-Chase Price ─── */}
        <div className="flex items-center gap-3 px-3 sm:px-4 py-3 rounded-lg bg-rose-950/30 border border-rose-500/20">
          <AlertTriangle className="w-4 h-4 text-rose-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-xs font-mono text-zinc-200 uppercase tracking-widest font-bold">{t('noChasePriceLabel')}</span>
            <p className="text-xs text-zinc-400 font-mono leading-tight mt-0.5">{t('noChaseDesc')}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-base sm:text-lg font-bold font-mono text-zinc-100 tabular-nums">{signal.noChasePrice}</p>
            <p className="text-[10px] text-zinc-400 font-mono tracking-widest">{t('hardLimit')}</p>
          </div>
        </div>

        {/* ─── TP Ladder ─── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-zinc-200 uppercase tracking-widest">{t('takeProfitTargets')}</span>
              <span className="text-[10px] font-mono text-zinc-500 bg-zinc-800/80 px-1.5 py-0.5 rounded-md border border-white/5">{signal.tpLevels.length} {t('levels')}</span>
            </div>
            <button
              onClick={() => setExpandedTPs(!expandedTPs)}
              className="flex items-center gap-0.5 text-[10px] font-mono text-zinc-500 hover:text-zinc-300 transition-all duration-150"
            >
              {expandedTPs ? <><ChevronUp className="w-3.5 h-3.5" /> {t('less')}</> : <><ChevronDown className="w-3.5 h-3.5" /> {t('all5')}</>}
            </button>
          </div>
          <div className="space-y-2">
            {visibleTPs.map((tp: TPLevel, idx: number) => (
              <TPRow key={`tp-level-${tp.level}`} tp={tp} idx={idx} isBuy={isBuy} directionColor={directionColor} totalLevels={signal.tpLevels.length} />
            ))}
          </div>
        </div>

        {/* ─── Timing Block ─── */}
        <div>
          <div className="flex items-center gap-1.5 mb-3">
            <Clock className="w-3.5 h-3.5 text-zinc-500" />
            <span className="text-xs font-bold text-zinc-200 uppercase tracking-widest">{t('timing')}</span>
          </div>

          {/* Countdown Timer */}
          <div className={`flex items-center justify-between px-3 sm:px-4 py-3 rounded-lg border mb-3 ${
            countdown.expired
              ? 'bg-zinc-800/40 border-zinc-700/40'
              : countdown.urgent
              ? 'bg-rose-950/40 border-rose-500/40 animate-pulse' :'bg-amber-950/20 border-amber-500/25'
          }`}>
            <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
              <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 border ${
                countdown.expired
                  ? 'bg-zinc-800/60 border-zinc-700/40'
                  : countdown.urgent
                  ? 'bg-rose-500/15 border-rose-500/30' :'bg-amber-500/10 border-amber-500/25'
              }`}>
                <Timer className={`w-3.5 h-3.5 ${
                  countdown.expired ? 'text-zinc-500' : countdown.urgent ? 'text-rose-400' : 'text-amber-400'
                }`} />
              </div>
              <div className="min-w-0">
                <p className={`text-[10px] font-mono uppercase tracking-widest font-bold ${
                  countdown.expired ? 'text-zinc-500' : countdown.urgent ? 'text-rose-300' : 'text-amber-300'
                }`}>
                  {countdown.expired ? 'Signal Expired' : countdown.urgent ? '⚠ Expiring Soon' : 'Time Remaining'}
                </p>
                <p className="text-[10px] text-zinc-500 font-mono truncate">
                  {countdown.expired ? 'No longer valid' : `Until ${signal.timing.validUntilTime} UTC`}
                </p>
              </div>
            </div>
            <div className={`text-right font-mono font-bold tabular-nums flex-shrink-0 ml-2 ${
              countdown.expired
                ? 'text-zinc-500 text-sm'
                : countdown.urgent
                ? 'text-rose-400 text-lg sm:text-xl' :'text-amber-300 text-lg sm:text-xl'
            }`}>
              {countdown.display}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TPRow({
  tp,
  idx,
  isBuy,
  directionColor,
  totalLevels,
}: {
  tp: TPLevel;
  idx: number;
  isBuy: boolean;
  directionColor: string;
  totalLevels: number;
}) {
  const isFirst = idx === 0;

  const tpColors = [
    { bar: isBuy ? '#34d399' : '#fb7185', glow: isBuy ? 'shadow-emerald-500/30' : 'shadow-rose-500/30', text: isBuy ? 'text-emerald-400' : 'text-rose-400', badge: isBuy ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25' : 'bg-rose-500/15 text-rose-300 border-rose-500/25' },
    { bar: isBuy ? '#6ee7b7' : '#fda4af', glow: isBuy ? 'shadow-emerald-400/20' : 'shadow-rose-400/20', text: isBuy ? 'text-emerald-300' : 'text-rose-300', badge: isBuy ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/20' },
    { bar: '#fbbf24', glow: 'shadow-amber-500/20', text: 'text-amber-400', badge: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
    { bar: '#f97316', glow: 'shadow-orange-500/15', text: 'text-orange-400', badge: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
    { bar: '#a78bfa', glow: 'shadow-violet-500/15', text: 'text-violet-400', badge: 'bg-violet-500/10 text-violet-400 border-violet-500/20' },
  ];

  const scheme = tpColors[Math.min(idx, tpColors.length - 1)];

  return (
    <div className={`rounded-lg border border-white/5 bg-zinc-900/60 hover:bg-zinc-800/60 hover:border-white/10 hover:-translate-y-0.5 transition-all duration-150 overflow-hidden group cursor-default ${isFirst ? `shadow-md ${scheme.glow}` : ''}`}>
      <div className="flex items-center gap-3 px-3.5 py-2.5">
        <div className={`w-9 h-6 rounded-md flex items-center justify-center flex-shrink-0 text-[10px] font-bold font-mono border ${scheme.badge}`}>
          TP{tp.level}
        </div>
        <div className="w-20 flex-shrink-0">
          <p className={`text-sm font-bold font-mono tabular-nums ${scheme.text}`}>{tp.price}</p>
        </div>
        <div className="flex-1 flex-shrink-0">
          <p className={`text-[11px] font-mono font-bold tabular-nums ${scheme.text}`}>+{tp.pips} pips</p>
        </div>
        <div className="w-10 text-right flex-shrink-0">
          <span className="text-[10px] font-mono text-zinc-600 group-hover:text-zinc-500">RR{tp.rr}</span>
        </div>
      </div>
      {isFirst && (
        <div
          className="h-px w-full"
          style={{ background: `linear-gradient(90deg, transparent, ${scheme.bar}50, transparent)` }}
        />
      )}
    </div>
  );
}

function TimingBlock({
  label,
  value,
  subValue,
  color,
  bg,
  border,
  icon,
}: {
  label: string;
  value: string;
  subValue: string;
  color: string;
  bg: string;
  border: string;
  icon: React.ReactNode;
}) {
  return (
    <div className={`rounded-lg border ${bg} ${border} p-3 transition-all duration-200 hover:-translate-y-0.5 cursor-default`}>
      <div className="flex items-center gap-1.5 mb-2">
        {icon}
        <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest truncate">{label}</span>
      </div>
      <p className={`text-sm font-bold font-mono tabular-nums ${color}`}>{value}</p>
      <p className="text-[10px] text-zinc-600 font-mono mt-1">{subValue}</p>
    </div>
  );
}